import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  completeSignIn,
  isReturningFromEmailLink,
  requestMagicLink,
} from '../lib/auth';
import { useAuth } from '../lib/useAuth';

export default function SignIn() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');

  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'idle' | 'sending' | 'sent' | 'completing' | 'error'>(
    'idle',
  );
  const [error, setError] = useState<string | null>(null);
  const [needEmailConfirm, setNeedEmailConfirm] = useState(false);

  useEffect(() => {
    if (!isReturningFromEmailLink()) return;
    const stored = window.localStorage.getItem('snippits.pendingEmail');
    if (!stored) {
      setNeedEmailConfirm(true);
      return;
    }
    setPhase('completing');
    completeSignIn(stored)
      .then(() => navigate(next ?? '/app', { replace: true }))
      .catch((err) => {
        setPhase('error');
        setError(err.message);
      });
  }, [navigate, next]);

  useEffect(() => {
    if (auth.status === 'signed-in' && phase === 'idle') {
      navigate(next ?? '/app', { replace: true });
    }
  }, [auth, phase, navigate, next]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPhase('sending');
    try {
      await requestMagicLink(email, next);
      setPhase('sent');
    } catch (err) {
      setPhase('error');
      setError((err as Error).message);
    }
  };

  const onConfirmEmail = async (e: FormEvent) => {
    e.preventDefault();
    setPhase('completing');
    try {
      await completeSignIn(email);
      navigate(next ?? '/app', { replace: true });
    } catch (err) {
      setPhase('error');
      setError((err as Error).message);
    }
  };

  if (phase === 'completing') {
    return (
      <div className="mx-auto flex max-w-sm flex-col gap-4 px-6 py-16">
        <p className="text-sm text-neutral-400">Finishing sign-in…</p>
      </div>
    );
  }

  if (phase === 'sent') {
    return (
      <div className="mx-auto flex max-w-sm flex-col gap-4 px-6 py-16">
        <h1 className="text-2xl font-semibold">Check your email</h1>
        <p className="text-sm text-neutral-400">
          We sent a sign-in link to <strong>{email}</strong>. Click it from the same browser to
          finish signing in.
        </p>
      </div>
    );
  }

  if (needEmailConfirm) {
    return (
      <form onSubmit={onConfirmEmail} className="mx-auto flex max-w-sm flex-col gap-4 px-6 py-16">
        <h1 className="text-2xl font-semibold">Confirm your email</h1>
        <p className="text-sm text-neutral-400">
          We need to confirm the address you used. Retype it to complete sign-in.
        </p>
        <input
          type="email"
          required
          autoFocus
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm"
        />
        <button
          type="submit"
          className="rounded-md bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600"
        >
          Finish sign-in
        </button>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </form>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mx-auto flex max-w-sm flex-col gap-4 px-6 py-16">
      <h1 className="text-2xl font-semibold">Sign in</h1>
      <p className="text-sm text-neutral-400">
        We&apos;ll email you a link &mdash; no password needed.
      </p>
      <input
        type="email"
        required
        autoFocus
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="rounded-md border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm"
      />
      <button
        type="submit"
        disabled={phase === 'sending' || !email}
        className="rounded-md bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
      >
        {phase === 'sending' ? 'Sending…' : 'Send sign-in link'}
      </button>
      {error && <p className="text-sm text-red-400">{error}</p>}
    </form>
  );
}
